import i18next from "i18next";

export const ATTACHMENT_DRAG_MIME = "application/x-vigepra-attachment";

export interface AttachmentDragPayload {
    id: number;
    name: string;
}

export type AttachmentReference = AttachmentDragPayload;

/** Get the configured file mention prefix (e.g. "fichero:" or "file:") */
export const getFileMentionPrefix = (): string => {
    const raw = i18next.t("developments.detail.file_mention_prefix");
    if (raw && !raw.startsWith("developments.detail.")) {
        return raw.endsWith(":") ? raw : `${raw}:`;
    }

    return "fichero:";
};

/** Visible mention inserted in the composer while typing or dragging. */
export const buildComposerAttachmentMention = (name: string): string => {
    const prefix = getFileMentionPrefix();
    const trimmed = name.trim();
    const hasSpace = /\s/.test(trimmed);
    return hasSpace ? `@${prefix}"${trimmed}"` : `@${prefix}${trimmed}`;
};

/** Stored reference to an existing attachment (aligned with the @ prefix mention). */
export const buildAttachmentReference = (id: number, name: string): string => {
    return `[${name.replace(/[\[\]]/g, "")}](attachment:${id})`;
};

export const insertAttachmentReference = (
    currentBody: string,
    _id: number,
    name: string
): string => {
    const reference = buildComposerAttachmentMention(name);
    const trimmed = currentBody.trim();
    if (!trimmed) {
        return reference;
    }

    const separator =
        currentBody.endsWith("\n") || currentBody.endsWith(" ") ? "" : " ";
    return `${currentBody}${separator}${reference}`;
};

/**
 * Expand attachment references before sending.
 * Mentions remain as @fichero:name or @fichero:"name with spaces".
 */
export const expandAttachmentReferences = (
    content: string,
    refs: AttachmentReference[]
): string => {
    const html = /<\/?(?:p|span|strong|em|div)\b/i.test(content);
    const escape = (text: string) =>
        text
            .replaceAll("&", "&amp;")
            .replaceAll('"', "&quot;")
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;");
    return refs.reduce((result, ref) => {
        const link = html
            ? `<button type="button" data-attachment-id="${ref.id}">${escape(ref.name)}</button>`
            : buildAttachmentReference(ref.id, ref.name);
        return result.replaceAll(
            buildComposerAttachmentMention(ref.name),
            link
        );
    }, content);
};

/**
 * Cleans legacy [name](attachment:id) references into modern @fichero:name for copying or AI.
 */
export const cleanAttachmentReferencesForCopy = (content: string): string => {
    if (!content) {
        return content;
    }

    const prefix = getFileMentionPrefix();
    return content.replace(
        /\[([^\]]+)\]\(attachment:\d+\)/g,
        (_match, name: string) => {
            const trimmed = name.trim();
            const hasSpace = /\s/.test(trimmed);
            return hasSpace ? `@${prefix}"${trimmed}"` : `@${prefix}${trimmed}`;
        }
    );
};

export const parseAttachmentDragPayload = (
    raw: string
): AttachmentDragPayload | null => {
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as AttachmentDragPayload;
        if (typeof parsed.id === "number" && typeof parsed.name === "string") {
            return parsed;
        }
    } catch {
        return null;
    }

    return null;
};
